import { useState, useEffect } from "react"
import { PieChart } from "@mui/x-charts/PieChart"
import { Autocomplete, TextField, Button, Box, useTheme, useMediaQuery } from "@mui/material"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import LoadingComponent from "../../../components/loadingComponent"

import months from "../assets/months"
import years from "../assets/years"
import BuscarIcon from "../../../assets/iconos/Buscar.ico"

export default function BasicPie({ pieData, isFetched, formattedCurrency }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"))
  const [chartSize, setChartSize] = useState({ width: 400, height: 200 })

  // Colores extendidos para el gráfico (30+ colores)
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7c7c",
    "#8dd1e1",
    "#d084d0",
    "#ffb347",
    "#87ceeb",
    "#dda0dd",
    "#98fb98",
    "#f0e68c",
    "#ff6347",
    "#40e0d0",
    "#ee82ee",
    "#90ee90",
    "#ff69b4",
    "#20b2aa",
    "#daa520",
    "#dc143c",
    "#00ced1",
    "#ff1493",
    "#00ff7f",
    "#ffd700",
    "#adff2f",
    "#ff4500",
    "#da70d6",
    "#32cd32",
    "#ff8c00",
    "#9370db",
    "#00fa9a",
    "#ff00ff",
    "#1e90ff",
    "#ffff54",
    "#ff6b6b",
    "#4ecdc4",
    "#45b7d1",
    "#f9ca24",
    "#f0932b",
    "#eb4d4b",
    "#6c5ce7",
    "#a29bfe",
    "#fd79a8",
    "#fdcb6e",
    "#e84393",
    "#00b894",
    "#00cec9",
    "#0984e3",
    "#74b9ff",
    "#e17055",
    "#81ecec",
  ]

  // Ajustar tamaño del gráfico según el dispositivo
  useEffect(() => {
    if (isMobile) {
      setChartSize({ width: 280, height: 200 })
    } else if (isTablet) {
      setChartSize({ width: 350, height: 220 })
    } else {
      setChartSize({ width: 420, height: 250 })
    }
  }, [isMobile, isTablet])

  if (!pieData || pieData.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          fontSize: "16px",
          color: "#666",
        }}
      >
        No existen datos
      </div>
    )
  }

  // Preparar datos para el gráfico con valores mínimos para visibilidad
  const processedPieData = pieData.map((obj, index) => {
    const absoluteValue = Math.abs(obj.value)
    // Asegurar que todos los valores sean visibles (mínimo 0.1% del total)
    const totalValue = pieData.reduce((acc, v) => acc + Math.abs(v.value), 0)
    const minVisibleValue = totalValue * 0.001 // 0.1% del total
    const displayValue = absoluteValue < minVisibleValue ? minVisibleValue : absoluteValue

    return {
      ...obj,
      value: displayValue,
      originalValue: absoluteValue, // Mantener el valor original para mostrar en la tabla
      color: colors[index % colors.length],
    }
  })

  const totalValue = pieData.reduce((acc, v) => acc + Math.abs(v.value), 0)

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "center" : "flex-start",
        justifyContent: "center",
        gap: isMobile ? 2 : 3,
        padding: isMobile ? 1 : 2,
        width: "100%",
        minHeight: "300px",
      }}
    >
      {/* Gráfico de pastel */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: chartSize.width,
          maxWidth: chartSize.width,
        }}
      >
        <PieChart
          series={[
            {
              data: processedPieData,
              highlightScope: { faded: "global", highlighted: "item" },
              faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
              innerRadius: 0,
              outerRadius: isMobile ? 80 : 100,
              paddingAngle: 1,
              cornerRadius: 2,
            },
          ]}
          width={chartSize.width}
          height={chartSize.height}
          slotProps={{
            legend: {
              hidden: true,
            },
            pieArcLabel: {
              hidden: true, // Ocultar etiquetas en el arco para evitar solapamiento
            },
          }}
          margin={{
            top: 20,
            bottom: 20,
            left: isMobile ? 10 : 20,
            right: isMobile ? 10 : 20,
          }}
        />
        <Box
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            marginTop: "10px",
            fontSize: isMobile ? "14px" : "16px",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            border: "1px solid #ddd",
          }}
        >
          Total Ventas: {formattedCurrency.format(totalValue)}
        </Box>
      </Box>

      {/* Leyenda personalizada */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          maxWidth: isMobile ? "100%" : "350px",
          width: "100%",
          maxHeight: isMobile ? "250px" : "300px",
          overflowY: "auto",
          padding: "8px",
          backgroundColor: "#fafafa",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            marginBottom: "8px",
            fontSize: isMobile ? "14px" : "16px",
            color: "#333",
            textAlign: "center",
            borderBottom: "2px solid #ddd",
            paddingBottom: "8px",
          }}
        >
          Detalle por Localidad
        </div>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            maxHeight: isMobile ? "180px" : "220px",
            overflowY: "auto",
            paddingRight: "4px",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "2px",
              "&:hover": {
                backgroundColor: "#555",
              },
            },
          }}
        >
          {pieData.map((item, index) => {
            const percentage = ((Math.abs(item.value) / totalValue) * 100).toFixed(1)
            const isSmallPercentage = parseFloat(percentage) < 1

            return (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "6px 8px",
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f8f8",
                  borderRadius: "6px",
                  border: isSmallPercentage ? "1px dashed #ccc" : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                    transform: "translateX(2px)",
                  },
                }}
              >
                {/* Indicador de color */}
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: colors[index % colors.length],
                    flexShrink: 0,
                    border: "1px solid #fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />

                {/* Contenido */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    width: "100%",
                    gap: isMobile ? 0 : 1,
                  }}
                >
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: isMobile ? "12px" : "13px",
                      wordBreak: "break-word",
                      lineHeight: "1.3",
                      color: "#333",
                      maxWidth: isMobile ? "100%" : "60%",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: isMobile ? "row" : "column",
                      alignItems: isMobile ? "flex-start" : "flex-end",
                      gap: isMobile ? "8px" : "2px",
                      fontSize: isMobile ? "11px" : "12px",
                      color: "#555",
                      flexShrink: 0,
                      fontWeight: "500",
                    }}
                  >
                    <span style={{ color: "#2e7d32" }}>{formattedCurrency.format(Math.abs(item.value))}</span>
                    <span
                      style={{
                        color: isSmallPercentage ? "#f57c00" : "#666",
                        fontWeight: isSmallPercentage ? "bold" : "normal",
                      }}
                    >
                      ({percentage}%)
                    </span>
                  </div>
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
